"use strict";

import Analytics from "/js/util/analytics.js";
import Util from "/js/util/util.js";
import CssUtil from "/js/util/css-util.js";
import AppUtil from "/js/util/app-util.js";
import Global from "/js/core/global.js";
import FadeUtil from "/js/util/fade-util.js";
import PrimerMaster from "/js/util/primermaster.js";
import Metrics from "/js/util/metrics.js";
import UserLessons from "/js/core/user-lessons.js";
import DoThisNowResultUtil from "/js/util/do-this-now-result-util.js";
import LessonJsonUtil from "/js/util/lesson-json-util.js";
import Strings from "/js/util/strings.js";
import FocusUtil from '/js/util/focus-util.js';
import Constants from '/js/core/constants.js';

/** 
 * Recap view
 */
export default class RecapView {

  static TABINDEX_DYNAMIC_CONTENT = 150;

  #$el = $('#recapView');

  #$recapContent = this.#$el.find("#recapContent");
  #$contentBottomArea = this.#$el.find(".bottomArea");
  #$nextLessonArea = this.#$el.find("#recapNextLesson");
  #$takeawaysSection = this.#$el.find("#recapTakeaways");
  #$takeawaysStroke = this.#$el.find("#takeawaysStroke");
  #$bonusSection = this.#$el.find("#recapBonus");
  #$doThisNowSection = this.#$el.find("#recapDoThisNow");

  #$mobileDownloadCta = this.#$el.find("recapMobileDownloadCta");
  #$replayButton = this.#$el.find("#recapReplayButton");
  #$nextLessonButton = this.#$el.find("#recapNextLessonButton");

  #$footerBackButton = $('#footerBackButton'); // not part of recap view

  #nextLessonId;
  #$tabIndexCache = [];

  constructor() {
    this.#$replayButton.on("click tap", e => this.#onReplayLessonButton(e));
    FocusUtil.doStandardTreatment(this.#$replayButton);

    this.#$nextLessonButton.on("click tap", e => this.#onStartNextLessonButton(e));
    FocusUtil.doStandardTreatment(this.#$nextLessonButton);
  }

  show() {
    this.resize();
    this.#populate();
    this.isEnabled = true;
    this.#$recapContent.scrollTop(0);
    FadeUtil.fadeIn(this.#$el);
    Util.windowUnselect();
    this.#$tabIndexCache = [];

    Analytics.trackLessonRecapView();
    Analytics.dashboardButtonTrackingSource = "recap";
  }

  get $el() {
    return this.#$el;
  }

  hide() {
    FadeUtil.fadeOut(this.#$el);
  }

  resize() {
    this.#sizeNextLessonArea();
  }

  set isEnabled(b) {

    if (b) {

      Util.setAriaHidden(this.#$el, false);
      this.#$mobileDownloadCta.attr("tabindex", Constants.TABINDEX_CONTENT_GENERAL);
      this.#$replayButton.attr("tabindex", Constants.TABINDEX_CONTENT_GENERAL + 1);
      this.#$nextLessonButton.attr("tabindex", Constants.TABINDEX_CONTENT_END);

      // Restore tabindex of dynamically generated elements
      for (const el of this.#$tabIndexCache) {
        $(el).attr("tabindex", RecapView.TABINDEX_DYNAMIC_CONTENT);
      }

    } else {

      Util.setAriaHidden(this.#$el, true);
      this.#$mobileDownloadCta.attr("tabindex", -1);
      this.#$replayButton.attr("tabindex", -1);
      this.#$nextLessonButton.attr("tabindex", -1);

      // Cache dynamic content elements that have a tabindex (viz, bonus links) and clear their tabindex values
      const selector = `[tabindex=${RecapView.TABINDEX_DYNAMIC_CONTENT}]`;
      this.#$tabIndexCache = this.#$contentBottomArea.find(selector);
      for (const el of this.#$tabIndexCache) {
        $(el).attr("tabindex", -1);
      }
    }
  }

  #populate() {
    this.#nextLessonId = AppUtil.getNextUnfinishedLesson(Global.lessonId);
    this.#populateNextLessonArea();
    this.#populateTopArea();
    this.#populateTakeaways();
    this.#populateBonus();
    this.#populateDoThisNow();
    this.#updateTakeawaysStroke();
  }

  #populateTopArea() {
    let metadata = PrimerMaster.getLessonById(Global.lessonId);

    const $title = this.#$el.find("#recapLessonTitle");
    let s = metadata ? (metadata.title || "") : "";
    $title.text(s);

    const localizationString = Strings.get('lesson_credit');
    const author = metadata ? metadata.credit : null;
    let creditLine;
    if (author) {
      if (localizationString) {
        creditLine = localizationString.replace("%1$s", author);
      } else {
        creditLine = author;
      }
    }
    const $credit = this.#$el.find("#recapLessonCredit");
    if (creditLine) {
      $credit.text(creditLine);
      $credit.show();
    } else {
      $credit.hide();
    }
  }

  #populateTakeaways() {
    const $el = this.#$takeawaysSection.find(".recapSectionContent");
    $el.html("");

    const card = Global.lessonData.takeawaysCard;
    if (!card) {
      return;
    }
    if (!card.text || !(card.text.length > 0)) {
      return;
    }

    let s = "<ol>";
    card.text.forEach((item, i) => {
      s += "<li>" + item.content + "</li>";
    });
    s += "</ol>";
    $el.html(s);
  }

  #populateBonus() {
    const data = Global.lessonData.bonus;
    if (!data) {
      this.#$bonusSection.hide();
      return;
    }

    // Procedurally construct html (not using template):
    let s = "";
    const copy = (data && data.text && data.text && data.text[0] && data.text[0].content)
        ? data.text[0].content
        : "";
    s += "<div class='copy'>" + copy + "</div>";

    const linkString = Strings.get("recap_bonus_link_label");
    if (data.links) {
      for (let item of data.links) {
        if (item.text && item.url) {
          s += `<div class="linkItem"><div class="linkCaption">${linkString}</div><a href="${item.url}" tabindex="${RecapView.TABINDEX_DYNAMIC_CONTENT}" target="_blank">${item.text}</a></div>`;
        }
      }
    }

    if (s.length === 0) {
      this.#$bonusSection.hide();
      return;
    }

    if (UserLessons.current.answers) {
      // Add separator between bonus and do-this-now sections.
      s += `<div class="bonusStroke"></div>`;
    }

    const $content = this.#$el.find("#recapBonus .recapSectionContent");
    $content.html(s);

    this.#$bonusSection.show();
  }

  #populateDoThisNow() {
    this.#$doThisNowSection.hide();
    const answers = UserLessons.current.answers;
    if (!answers) {
      return;
    }
    const interaction = LessonJsonUtil.getInteractionById(answers.interactionId, Global.lessonData);
    if (!interaction) {
      cl("Interaction id not found:", answers.interactionId);
      return;
    }

    const $content = this.#$el.find("#recapDoThisNow .recapSectionContent");
    DoThisNowResultUtil.makeHtml(interaction, answers, (html) => {  // Rem, is potentially async:
      const $newContent = $(html);
      $content.replaceWith($newContent);
      this.#$doThisNowSection.show();
      this.#updateTakeawaysStroke();
    });
  }

  #populateNextLessonArea() {
    const $nextLessonHolder =  this.$el.find("#recapNextLesson");

    if (!this.#nextLessonId) {
      $nextLessonHolder.hide();
      return;
    }
    $nextLessonHolder.show();

    let metadata = PrimerMaster.getLessonById(this.#nextLessonId);
    const $nextLessonTitle = this.$el.find("#recapNextLessonTitle");
    let s = metadata ? (metadata.title || "") : "";
    $nextLessonTitle.text(s);
  }

  #updateTakeawaysStroke() {
    const b =  (CssUtil.isDisplayBlock(this.#$bonusSection) || CssUtil.isDisplayBlock(this.#$doThisNowSection));
    b ? this.#$takeawaysStroke.show() : this.#$takeawaysStroke.hide();
  }

  #sizeNextLessonArea() {
    if (Metrics.isMobileWidth) {
      // Erase any values set from when desktop width
      this.#$nextLessonArea.width("");
      this.#$nextLessonArea.css("margin-left", "");
      return;
    }

    const recapWidth = this.$el.width();
    let backButtonWidth = parseFloat(this.#$footerBackButton.css('left')) || 24; // left margin
    backButtonWidth += this.#$footerBackButton.outerWidth()
    backButtonWidth += 24; // right margin
    const primerLogoWidth = 200; // hardcoded, good enough

    let w = recapWidth - backButtonWidth - primerLogoWidth;
    w = Math.min(w, Metrics.mobileMaxWidth);
    w = Math.floor(w);

    let x = (recapWidth - w) / 2;
    x = Math.max(x, backButtonWidth);

    this.#$nextLessonArea.width(w + "px");
    this.#$nextLessonArea.css("margin-left", x + "px");
  }

  #onReplayLessonButton(e) {
    Analytics.trackLessonRecapReplay();
    this.#$el.trigger("recap-view-replay");
  }

  #onStartNextLessonButton(e) {
    const isRepeatedStart = UserLessons.getByLessonId(this.#nextLessonId) != null;
    Analytics.trackLessonStartFromRecap(isRepeatedStart, UserLessons.totalViewedLessonsCount, this.#nextLessonId);
    Analytics.lessonTrackingSource = "recap-next-lesson";
    this.#$el.trigger("next-lesson-button", this.#nextLessonId);
  }
}
